import { CompoundingPeriod, VoucherTypeSetting } from '../../vouchers/index.js';
import { parseDate } from '../utils.js';
export const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;
export const getInterestRatesGen = (rates) => {
    return (from, to) => {
        const interests = [];
        for (let i = 0; i < rates.length; i++) {
            const rate = rates[i];
            if (!rate.time) {
                throw new Error('Interest rates not set correctly');
            }
            if (rate.time >= to) {
                continue;
            }
            else if (rate.time > from) {
                interests.push(rate);
            }
            else {
                interests.push(rate);
                break;
            }
        }
        return interests.length ? interests.reverse() : [];
    };
};
export const addAmountToVoucher = (caseVoucher, amount, hist) => {
    caseVoucher.amountUpdated = true;
    caseVoucher.amount += amount;
    if (hist) {
        const interestHistory = caseVoucher.interestHistory || [];
        const lastHistItem = interestHistory[interestHistory.length - 1];
        if (caseVoucher.interestHistory &&
            lastHistItem &&
            lastHistItem.interest === hist.interest &&
            lastHistItem.referenceVoucherBalance === hist.referenceVoucherBalance &&
            lastHistItem.to === hist.from &&
            lastHistItem.capitalizedInterestBalance ===
                hist.capitalizedInterestBalance) {
            caseVoucher.interestHistory[interestHistory.length - 1] = {
                from: lastHistItem.from,
                to: hist.to,
                amount: lastHistItem.amount + amount,
                interest: lastHistItem.interest,
                referenceVoucherBalance: lastHistItem.referenceVoucherBalance,
                capitalizedInterestBalance: hist.capitalizedInterestBalance,
            };
        }
        else {
            if (caseVoucher.interestHistory != null) {
                caseVoucher.interestHistory.push({
                    ...hist,
                    amount,
                });
            }
            else {
                caseVoucher.interestHistory = [
                    {
                        ...hist,
                        amount,
                    },
                ];
            }
        }
    }
    return caseVoucher;
};
export var BreakpointType;
(function (BreakpointType) {
    BreakpointType["InterestChange"] = "INTEREST_CHANGE";
    BreakpointType["InterestDailyCompoundingPeriod"] = "INTEREST_DAILY_COMPOUNDING_PERIOD";
    BreakpointType["InterestMonthlyCompoundingPeriod"] = "INTEREST_MONTHLY_COMPOUNDING_PERIOD";
    BreakpointType["InterestQuarterlyCompoundingPeriod"] = "INTEREST_QUARTERLY_COMPOUNDING_PERIOD";
    BreakpointType["InterestStartDate"] = "INTEREST_START_DATE";
    BreakpointType["InterestEndDate"] = "INTEREST_END_DATE";
})(BreakpointType || (BreakpointType = {}));
export const COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE = {
    [CompoundingPeriod.Daily]: BreakpointType.InterestDailyCompoundingPeriod,
    [CompoundingPeriod.Monthly]: BreakpointType.InterestMonthlyCompoundingPeriod,
    [CompoundingPeriod.Quarterly]: BreakpointType.InterestQuarterlyCompoundingPeriod,
};
const getBreakpointsInPeriod = (orderedBreakpoints, from, to) => {
    let breakpoints = [];
    for (let i = 0; i < orderedBreakpoints.length; i++) {
        const breakpoint = orderedBreakpoints[i];
        if (breakpoint.date < from) {
            continue;
        }
        if (breakpoint.date >= to) {
            break;
        }
        breakpoints.push(breakpoint);
    }
    return breakpoints;
};
export const withBreakpoints = (allOrderedBreakpoints, until, currState, prevState, voucher, nextVoucher, fn) => {
    if (!fn) {
        throw new Error('No function provided');
    }
    let state = currState;
    const periodStart = voucher.parsedDate;
    const d = nextVoucher && nextVoucher.parsedDate;
    const periodEnd = !d || until < d ? until : d || until;
    if (periodStart > periodEnd) {
        return state;
    }
    let breakpointsInPeriod = getBreakpointsInPeriod(allOrderedBreakpoints, periodStart, periodEnd);
    let from = periodStart;
    for (let i = 0; i < breakpointsInPeriod.length; i++) {
        const breakpoint = breakpointsInPeriod[i];
        state = fn(currState, prevState, from, breakpoint.date, breakpoint.type);
        from = breakpoint.date;
    }
    state = fn(currState, prevState, from, periodEnd);
    return state;
};
export const getOrderedBreakpoints = (caseVouchers, until) => {
    let breakpoints = [];
    let containsDailyCompound = false;
    let containsMonthlyCompound = false;
    let containsQuarterlyCompound = false;
    for (let i = 0; i < caseVouchers.length; i++) {
        const caseVoucher = caseVouchers[i];
        if (caseVoucher.parsedInterestStartDate) {
            breakpoints.push({
                type: BreakpointType.InterestStartDate,
                date: caseVoucher.parsedInterestStartDate,
            });
        }
        if (caseVoucher.parsedInterestEndDate) {
            breakpoints.push({
                type: BreakpointType.InterestEndDate,
                date: caseVoucher.parsedInterestEndDate,
            });
        }
        if (caseVoucher.setting === VoucherTypeSetting.CompoundInterest) {
            if (caseVoucher.settingDetails.compoundingPeriod === CompoundingPeriod.Daily) {
                containsDailyCompound = true;
            }
            if (caseVoucher.settingDetails.compoundingPeriod ===
                CompoundingPeriod.Monthly) {
                containsMonthlyCompound = true;
            }
            if (caseVoucher.settingDetails.compoundingPeriod ===
                CompoundingPeriod.Quarterly) {
                containsQuarterlyCompound = true;
            }
        }
    }
    const dailyCompoundingBreakpoints = containsDailyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestDailyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Daily), until, (d) => {
            const nextDay = new Date(d);
            nextDay.setUTCDate(nextDay.getUTCDate() + 1);
            return nextDay;
        }, (d) => {
            const startOfDay = new Date(d);
            startOfDay.setUTCHours(0, 0, 0, 0);
            return startOfDay;
        })
        : [];
    const monthlyCompoundingBreakpoints = containsMonthlyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestMonthlyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Monthly), until, (d) => {
            const nextMonth = new Date(d);
            nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
            return nextMonth;
        }, (d) => {
            const startOfMonth = new Date(d);
            startOfMonth.setUTCDate(1);
            startOfMonth.setUTCHours(0, 0, 0, 0);
            return startOfMonth;
        })
        : [];
    const quarterlyCompoundingBreakpoints = containsQuarterlyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestQuarterlyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Quarterly), until, (d) => {
            const nextQuarter = new Date(d);
            nextQuarter.setUTCMonth(nextQuarter.getUTCMonth() + 3);
            return nextQuarter;
        }, (d) => {
            const startOfQuarter = new Date(d);
            startOfQuarter.setUTCMonth(Math.floor(startOfQuarter.getUTCMonth() / 3) * 3);
            startOfQuarter.setUTCDate(1);
            startOfQuarter.setUTCHours(0, 0, 0, 0);
            return startOfQuarter;
        })
        : [];
    breakpoints = breakpoints.concat(dailyCompoundingBreakpoints, monthlyCompoundingBreakpoints, quarterlyCompoundingBreakpoints);
    return breakpoints.sort((a, b) => a.date - b.date);
};
const checkIfDateIsBefore = (compoundingDate, before) => {
    return compoundingDate <= parseDate(new Date(before).toISOString());
};
const getCompoundingBreakpoints = (type, caseVouchers, until, dateIncrementer, firstCompoundingDate) => {
    if (!caseVouchers.length) {
        return [];
    }
    const earliestVoucherDate = caseVouchers
        .map((v) => new Date(v.date))
        .sort((a, b) => a.getTime() - b.getTime())[0];
    let compoundingDate = firstCompoundingDate(earliestVoucherDate);
    let compoundingDates = [];
    while (checkIfDateIsBefore(compoundingDate.getTime(), until)) {
        compoundingDates.push({ date: compoundingDate.getTime(), type: type });
        compoundingDate = dateIncrementer(compoundingDate);
    }
    return compoundingDates;
};
//# sourceMappingURL=common.js.map